// Portal ticket detail — subject/body + comment thread (VII-207).

import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack, TextField, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import portalApi from '../api';
import { getPortalEmail } from '../auth';

function fmtDate(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'dd.MM.yyyy HH:mm'); } catch { return iso; }
}

export default function TicketDetail() {
  const { providerId, ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const ownEmail = getPortalEmail();

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const [tRes, cRes] = await Promise.all([
        portalApi.get(`/portal/tickets/${providerId}/${ticketId}`),
        portalApi.get(`/portal/tickets/${providerId}/${ticketId}/comments`),
      ]);
      setTicket(tRes.data);
      setComments(cRes.data || []);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load ticket.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [providerId, ticketId]);

  const handlePost = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    try {
      await portalApi.post(`/portal/tickets/${providerId}/${ticketId}/comments`, { content: draft.trim() });
      setDraft('');
      await reload();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to post comment.');
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  if (error && !ticket) return (
    <Stack spacing={2}>
      <Alert severity="error">{error}</Alert>
      <Button variant="outlined" onClick={() => navigate('/portal/tickets')}>Back to tickets</Button>
    </Stack>
  );
  if (!ticket) return null;

  return (
    <Box>
      <Button onClick={() => navigate('/portal/tickets')} sx={{ mb: 2 }}>← Back</Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary">#{ticket.ticket_number}</Typography>
          <Chip size="small" label={ticket.stage_key} />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{ticket.provider_name}</Typography>
        </Stack>
        <Typography variant="h5" gutterBottom>{ticket.subject}</Typography>
        {ticket.description && (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 2, color: 'text.secondary' }}>
            {ticket.description}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Opened {fmtDate(ticket.created_at)}
        </Typography>
      </Paper>

      <Typography variant="h6" sx={{ mb: 2 }}>Conversation</Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        {comments.length === 0 ? (
          <Alert severity="info" sx={{ bgcolor: 'transparent' }} variant="outlined">
            No replies yet.
          </Alert>
        ) : comments.map((c) => {
          const isOwn = c.user_id?.startsWith('portal:');
          return (
            <Paper key={c.id} variant="outlined" sx={{ p: 2, bgcolor: isOwn ? 'action.hover' : 'background.paper' }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {isOwn ? (ownEmail || 'You') : 'Support'}
                </Typography>
                <Typography variant="caption" color="text.secondary">{fmtDate(c.created_at)}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.content}</Typography>
            </Paper>
          );
        })}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Divider sx={{ mb: 2 }} />

      <Stack spacing={1}>
        <TextField
          label="Add a reply"
          multiline
          minRows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={posting}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handlePost} disabled={posting || !draft.trim()}>
            {posting ? 'Sending…' : 'Send reply'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
